import { BarcodeOutlined, DownloadOutlined, FilePdfOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ActionField, ActionMeta, LabelsPreview } from "@wms/domain";
import { Alert, App, Button, Col, Descriptions, Empty, Form, Grid, Input, InputNumber, Modal, Row, Select, Space, Spin, Switch, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { api, describeError } from "../api/client";
import type { Page } from "../api/client";
import { useAuthStore } from "../auth/store";
import { RefSelect } from "./RefSelect";

interface Props {
  entity: string;
  id: string;
  action: ActionMeta;
  row?: Record<string, unknown>;
  onClose: () => void;
}

interface Printer {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

const COPIES = new Set(["copies", "places"]);
// Chrome on Android has no PDF viewer inside a page: there the preview is a list and the PDF opens in a tab
const INLINE_PDF = typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false;

function initialValues(fields: ActionField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === "enum") out[f.name] = f.default ?? f.options?.[0]?.value;
    else if (f.type === "boolean") out[f.name] = false;
    else if (f.type === "number" && COPIES.has(f.name)) out[f.name] = f.name === "copies" ? 1 : undefined;
  }
  return out;
}

/** Options as the server reads them: empty values are left out. */
function clean(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values ?? {}).filter(([, v]) => v !== undefined && v !== null && v !== ""));
}

/**
 * «Этикетки и штрихкоды» (28.10.2026): the options of the card on the left, the very PDF that will be printed on the
 * right. The preview is rebuilt on every change; «Скачать PDF» is open only once the preview of these options is in
 * sight, so a label is never printed unseen. Products without a barcode are named and get internal EAN-13 codes by
 * an explicit button; a receipt can also be sent to the label printer queue.
 */
export function LabelsModal({ entity, id, action, row, onClose }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;
  const target = action.preview ?? "";
  const fields = useMemo(() => action.fields ?? [], [action.fields]);
  const initial = useMemo(() => initialValues(fields), [fields]);
  const [form] = Form.useForm();
  const watched = Form.useWatch([], form) as Record<string, unknown> | undefined;
  const [options, setOptions] = useState<Record<string, unknown>>(clean(initial));
  const [version, setVersion] = useState(0);
  const warehouseId = String((entity === "warehouses" ? row?.id : row?.warehouse_id) ?? "");

  // the preview follows the options with a short pause, not on every keystroke
  const watchedKey = JSON.stringify(clean(watched ?? initial));
  useEffect(() => {
    const timer = setTimeout(() => setOptions(JSON.parse(watchedKey)), 400);
    return () => clearTimeout(timer);
  }, [watchedKey]);

  const body = { target, object_id: id, options };
  const optionsKey = JSON.stringify(options);
  const preview = useQuery({
    queryKey: ["labels-preview", target, id, optionsKey, version],
    queryFn: () => api.post<LabelsPreview>("/labels/preview", body),
  });
  const pdf = useQuery({
    queryKey: ["labels-pdf", target, id, optionsKey, version],
    queryFn: async () => URL.createObjectURL((await api.blob("/labels/pdf", body)).blob),
    staleTime: Infinity,
  });
  useEffect(() => {
    const url = pdf.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [pdf.data]);
  const ready = preview.isSuccess && pdf.isSuccess && !preview.isFetching && !pdf.isFetching && optionsKey === JSON.stringify(clean(watched ?? initial));

  const download = useMutation({
    mutationFn: () => api.blob("/labels/pdf?download=true", body),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? "labels.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      message.success(`PDF скачан: этикеток ${preview.data?.count ?? ""}`);
      queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e) => message.error(describeError(e), 6),
  });

  const createBarcodes = useMutation({
    mutationFn: () => api.post<{ created: { object: string; barcode: string; package: string }[]; count: number }>("/labels/create-barcodes", body),
    onSuccess: (data) => {
      message.success(data.count ? `Создано штрихкодов: ${data.count} (${data.created.map((c) => `${c.object} → ${c.barcode}`).join(", ")})` : "Все товары уже со своими штрихкодами", 8);
      queryClient.invalidateQueries({ queryKey: ["barcodes"] });
      setVersion((v) => v + 1);
    },
    onError: (e) => message.error(describeError(e), 6),
  });

  const queueable = target === "receipt" && options.mode !== "containers";
  const printers = useQuery({
    queryKey: ["printers", "for-labels", warehouseId],
    // printers of this warehouse and the shared ones (no warehouse)
    queryFn: () =>
      api.get<Page<Printer>>("/printers", {
        size: 50,
        filter: {
          and: [
            { field: "is_active", op: "eq", value: true },
            ...(warehouseId ? [{ or: [{ field: "warehouse_id", op: "eq", value: warehouseId }, { field: "warehouse_id", op: "is_null" }] }] : []),
          ],
        },
      }),
    enabled: queueable && can("printing.printers.read"),
  });
  const [printerId, setPrinterId] = useState<string | undefined>();
  const toPrinter = useMutation({
    mutationFn: () =>
      api.post<{ labels: number; jobs: number }>(`/expected_receipts/${id}/print-labels`, { mode: options.mode, source: options.source, printer_id: printerId }),
    onSuccess: (data) => message.success(`В очереди принтера: этикеток ${data.labels} (заданий ${data.jobs})`, 6),
    onError: (e) => message.error(describeError(e), 6),
  });

  const control = (f: ActionField) => {
    switch (f.type) {
      case "enum":
        return <Select options={f.options ?? []} />;
      case "boolean":
        return <Switch checkedChildren="Да" unCheckedChildren="Нет" />;
      case "number":
        return <InputNumber min={1} max={1000} style={{ width: "100%" }} placeholder={f.name === "places" ? "по числу мест" : undefined} />;
      case "ref":
        return <RefSelect entity={f.ref!} filter={warehouseId ? { field: "warehouse_id", op: "eq", value: warehouseId } : null} />;
      default:
        return <Input allowClear />;
    }
  };

  const data = preview.data;
  const missing = data?.missing_barcodes ?? 0;
  const canCreate = missing > 0 && (target === "receipt" || target === "storage_object") && can("nsi.barcodes.write");
  // the missing barcodes have their own block with the button when they can be created here
  const problems = (data?.problems ?? []).filter((p) => !(canCreate && p.startsWith("Без своего штрихкода")));

  return (
    <Modal
      title={
        <Space>
          <BarcodeOutlined />
          {action.label}
        </Space>
      }
      open
      onCancel={onClose}
      width={narrow ? "calc(100vw - 16px)" : 1180}
      style={narrow ? { top: 8, maxWidth: "calc(100vw - 16px)" } : { top: 24 }}
      destroyOnHidden
      footer={
        <Space wrap style={{ justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Закрыть</Button>
          {queueable && (printers.data?.items.length ?? 0) > 0 ? (
            <Space.Compact>
              <Select
                style={{ minWidth: 200 }}
                placeholder="Принтер склада"
                allowClear
                value={printerId}
                onChange={setPrinterId}
                options={(printers.data?.items ?? []).map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
              />
              <Button icon={<PrinterOutlined />} disabled={!ready} loading={toPrinter.isPending} onClick={() => toPrinter.mutate()}>
                Отправить на принтер
              </Button>
            </Space.Compact>
          ) : null}
          <Button type="primary" icon={<DownloadOutlined />} disabled={!ready || !data?.count} loading={download.isPending} onClick={() => download.mutate()} data-testid="labels-download">
            Скачать PDF
          </Button>
        </Space>
      }
    >
      <Row gutter={[20, 16]}>
        <Col xs={24} md={9}>
          <Form form={form} layout="vertical" initialValues={initial} requiredMark={false}>
            {fields.map((f) => (
              <Form.Item key={f.name} name={f.name} label={f.label} valuePropName={f.type === "boolean" ? "checked" : "value"} style={{ marginBottom: 12 }}>
                {control(f)}
              </Form.Item>
            ))}
          </Form>
          {preview.isError ? <Alert type="error" showIcon message="Превью не построено" description={describeError(preview.error)} /> : null}
          {data ? (
            <>
              <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
                <Descriptions.Item label="Этикеток">{data.total > data.count ? `${data.count} из ${data.total}` : data.count}</Descriptions.Item>
                <Descriptions.Item label="Страниц PDF">{data.pages}</Descriptions.Item>
                <Descriptions.Item label="Формат">{data.format_label}</Descriptions.Item>
              </Descriptions>
              {problems.length ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Проверьте перед печатью"
                  description={
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {problems.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  }
                />
              ) : null}
              {canCreate ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`Без своего штрихкода товаров: ${missing}`}
                  description={
                    <>
                      <div style={{ marginBottom: 8 }}>Создадим внутренние EAN-13 (префикс из настроек) и запишем их в «Штрихкоды» товара — терминал прочитает напечатанную этикетку.</div>
                      <Button size="small" type="primary" icon={<BarcodeOutlined />} loading={createBarcodes.isPending} onClick={() => createBarcodes.mutate()} data-testid="labels-create-barcodes">
                        Создать штрихкоды
                      </Button>
                    </>
                  }
                />
              ) : null}
            </>
          ) : null}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            Справа — тот же PDF, который будет скачан или напечатан. Печать из браузера: масштаб 100 %, без полей и колонтитулов.
          </Typography.Paragraph>
        </Col>
        <Col xs={24} md={15}>
          <div style={{ position: "relative", height: narrow ? 420 : 640, border: "1px solid var(--wms-border)", borderRadius: 8, overflow: "hidden", background: "var(--wms-surface-2)" }}>
            {pdf.data && INLINE_PDF ? <iframe title="Превью этикеток" src={`${pdf.data}#toolbar=1&navpanes=0&view=FitH`} style={{ width: "100%", height: "100%", border: 0 }} data-testid="labels-preview" /> : null}
            {pdf.data && !INLINE_PDF ? (
              // a phone browser without a built-in PDF viewer: the labels as a list and the PDF in its own tab
              <div style={{ height: "100%", overflow: "auto", padding: 12 }} data-testid="labels-preview">
                <Button block icon={<FilePdfOutlined />} href={pdf.data} target="_blank" style={{ marginBottom: 12 }}>
                  Открыть превью PDF
                </Button>
                {(data?.items ?? []).map((item, i) => (
                  <div key={i} style={{ padding: "8px 10px", marginBottom: 6, borderRadius: 6, background: "var(--wms-surface)", border: "1px solid var(--wms-border)" }}>
                    <Typography.Text strong ellipsis style={{ display: "block" }}>
                      {item.title}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontFamily: "monospace" }}>
                      {item.code ?? "без штрихкода"}
                    </Typography.Text>
                  </div>
                ))}
                {data && data.count > data.items.length ? <Typography.Text type="secondary">…и ещё {data.count - data.items.length}</Typography.Text> : null}
              </div>
            ) : null}
            {pdf.isFetching || preview.isFetching ? (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.55)" }}>
                <Spin tip="Готовим превью…">
                  <div style={{ width: 120, height: 60 }} />
                </Spin>
              </div>
            ) : null}
            {pdf.isError ? (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Empty description={describeError(pdf.error)}>
                  <Button icon={<ReloadOutlined />} onClick={() => setVersion((v) => v + 1)}>
                    Повторить
                  </Button>
                </Empty>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </Modal>
  );
}
